import React from 'react'
import { Availability } from '@clear-denver/tpc-bff-client/build/api/tpc-bff/types'
import { Box, Text } from '@clear/design-system'
import { AccessibleLocation } from './AccessibleLocation'
import { ConversionCouponInfo } from './ConversionCouponInfo'
import { useFlag } from '@hooks/useFlag'

export interface ILaneInfo {
  name: string
  description: string
  openDaily: boolean
  isAccessible: boolean
  accessibilityMessage: string
  availability: Availability[]
  id: string
  lastLane?: boolean
  conversionCouponInfo?: string
  importantInfo?: string
}

const V1LaneInfo = ({
  name,
  description,
  openDaily,
  isAccessible,
  accessibilityMessage,
  availability,
  importantInfo,
  conversionCouponInfo,
  lastLane = false,
  displayConversionCouponInfo,
}) => {
  return (
    <>
      <Text data-testid="v1-lane-name" my={1}>
        {name}
      </Text>
      <Text data-testid="v1-lane-description">{description}</Text>
      <Box mt={1}>
        {availability.map(({ hours, days }, i) => (
          <div key={`${hours}-${i}`} className="mb-1">
            {
              <Text data-testid="v1-lane-day-availability" mb={2}>
                {!days && openDaily ? 'Open Daily' : days}
              </Text>
            }{' '}
            <Text data-testid="v1-lane-hour-availability" mb={2}>
              {hours}
            </Text>
          </div>
        ))}
        {importantInfo ? (
          <Text
            data-testid="v1-lane-important-info"
            mb={3}
            color="utilityDarkErrorRed"
          >
            {importantInfo}
          </Text>
        ) : null}
        {lastLane && conversionCouponInfo && displayConversionCouponInfo ? (
          <ConversionCouponInfo conversionCouponInfo={conversionCouponInfo} />
        ) : null}
      </Box>
      <AccessibleLocation
        isAccessible={isAccessible}
        accessibilityMessage={accessibilityMessage}
      />
    </>
  )
}

const V2LaneInfo = ({
  name,
  openDaily,
  availability,
  importantInfo,
  conversionCouponInfo,
  lastLane = false,
  displayConversionCouponInfo,
}) => {
  return (
    <>
      {name && (
        <Text
          data-testid="v2-lane-name"
          fontSize={1}
          fontWeight={400}
          lineHeight="1.125rem"
        >
          {name}
        </Text>
      )}
      <Box
        style={{
          display: 'grid',
          gap: '0.375rem',
        }}
      >
        {availability.map(({ hours, days }, i) => (
          <div key={`${hours}-${i}`}>
            {
              <Text
                data-testid="v2-lane-day-availability"
                fontSize={1}
                lineHeight="1.125rem"
              >
                Open {!days && openDaily ? 'Daily' : days}
              </Text>
            }{' '}
            <Text
              data-testid="v2-lane-hour-availability"
              fontSize={1}
              lineHeight="1.125rem"
            >
              {hours}
            </Text>
          </div>
        ))}
        {importantInfo ? (
          <Text
            data-testid="v2-lane-important-info"
            color="utilityDarkErrorRed"
            fontSize={1}
          >
            {importantInfo}
          </Text>
        ) : null}
        {lastLane && conversionCouponInfo && displayConversionCouponInfo ? (
          <ConversionCouponInfo conversionCouponInfo={conversionCouponInfo} />
        ) : null}
      </Box>
    </>
  )
}

export const LaneInfo = ({
  name,
  description,
  openDaily,
  isAccessible,
  accessibilityMessage,
  availability,
  importantInfo,
  conversionCouponInfo,
  lastLane = false,
}: ILaneInfo) => {
  const displayConversionCouponInfo =
    useFlag('locations')?.displayConversionCouponInfo
  const displayLocationSearchRevisedLayout =
    useFlag('layoutRefresh')?.displayLocationSearchRevisedLayout
  if (!displayLocationSearchRevisedLayout) {
    return (
      <V1LaneInfo
        name={name}
        description={description}
        openDaily={openDaily}
        isAccessible={isAccessible}
        accessibilityMessage={accessibilityMessage}
        availability={availability}
        importantInfo={importantInfo}
        conversionCouponInfo={conversionCouponInfo}
        lastLane={lastLane}
        displayConversionCouponInfo={displayConversionCouponInfo}
      />
    )
  } else {
    return (
      <V2LaneInfo
        name={name}
        openDaily={openDaily}
        availability={availability}
        importantInfo={importantInfo}
        conversionCouponInfo={conversionCouponInfo}
        lastLane={lastLane}
        displayConversionCouponInfo={displayConversionCouponInfo}
      />
    )
  }
}
